import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { createOfflineConversion } from '../../../api/admin';

const STAGE_OPTIONS = [
  { value: 'qualified', label: 'Qualified' },
  { value: 'meeting_held', label: 'Meeting held' },
  { value: 'proposal_sent', label: 'Proposal sent' },
  { value: 'contract_won', label: 'Contract won' },
];

const initialFormData = {
  email: '',
  stage: 'qualified',
  value_cents: '',
  currency: 'USD',
  note: '',
};

const inputClass =
  'w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600';

const OfflineConversions = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [result, setResult] = useState(null);

  const mutation = useMutation(createOfflineConversion, {
    onSuccess: (data) => {
      setResult({ type: 'success', data });
      setFormData(initialFormData);
    },
    onError: (error) => {
      setResult({
        type: 'error',
        message: error?.response?.data?.message || error.message,
      });
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setResult(null);

    mutation.mutate({
      ...formData,
      email: formData.email.trim(),
      currency: formData.currency.trim().toUpperCase(),
      note: formData.note.trim(),
    });
  };

  const isFormValid = () =>
    formData.email.trim() &&
    formData.stage &&
    formData.value_cents.toString().match(/^\d+$/) &&
    formData.currency.trim();

  return (
    <div className="p-8" style={{ maxWidth: '760px' }}>
      <h1 className="text-2xl font-bold mb-2">
        Enterprise-qualified conversation
      </h1>
      <p className="mb-8 text-gray-700">
        Log high-value offline enterprise outcomes in GA4 using the visitor
        attribution captured during the ALEX trial flow.
      </p>

      {result?.type === 'success' && (
        <div className="mb-6 p-4 rounded-lg bg-green-100 text-green-900">
          <strong>Offline conversion logged.</strong>
          <div className="mt-2 text-sm">
            GA4 events fired: {result.data?.ga4_success_count || 0} of{' '}
            {result.data?.ga4_request_count || 0}
            {result.data?.offline_only ? ' (offline-only attribution)' : ''}
          </div>
        </div>
      )}

      {result?.type === 'error' && (
        <div className="mb-6 p-4 rounded-lg bg-red-100 text-red-900">
          <strong>Unable to log offline conversion.</strong>
          <div className="mt-2 text-sm">{result.message}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: '20px' }}>
        <div>
          <label className="block text-sm font-semibold mb-2" htmlFor="email">
            EMAIL *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className={inputClass}
            placeholder="buyer@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" htmlFor="stage">
            STAGE *
          </label>
          <select
            id="stage"
            name="stage"
            value={formData.stage}
            onChange={handleChange}
            className={inputClass}
            required
          >
            {STAGE_OPTIONS.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex" style={{ gap: '16px' }}>
          <div className="flex-1">
            <label
              className="block text-sm font-semibold mb-2"
              htmlFor="value_cents"
            >
              VALUE CENTS *
            </label>
            <input
              id="value_cents"
              name="value_cents"
              type="number"
              min="0"
              step="1"
              value={formData.value_cents}
              onChange={handleChange}
              className={inputClass}
              placeholder="2500000"
              required
            />
          </div>

          <div style={{ width: '140px' }}>
            <label
              className="block text-sm font-semibold mb-2"
              htmlFor="currency"
            >
              CURRENCY *
            </label>
            <input
              id="currency"
              name="currency"
              type="text"
              value={formData.currency}
              onChange={handleChange}
              className={inputClass}
              maxLength={3}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" htmlFor="note">
            NOTE
          </label>
          <textarea
            id="note"
            name="note"
            value={formData.note}
            onChange={handleChange}
            className={inputClass}
            rows="4"
            placeholder="Optional context for audit history"
          />
        </div>

        <button
          className="p-4 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          type="submit"
          disabled={!isFormValid() || mutation.isLoading}
        >
          {mutation.isLoading ? 'Logging conversion...' : 'Log offline conversion'}
        </button>
      </form>
    </div>
  );
};

export default OfflineConversions;
